import React, { useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import {
  Box, Typography, Switch, TextField, Select, MenuItem, FormControl, InputLabel,
  IconButton, Tooltip, Divider,
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import BulletFieldEditor from './BulletFieldEditor';

const COL_ITEM_TYPE = 'SETTINGS_COLUMN';

const COLUMN_TYPES = ['text', 'currency', 'select', 'date', 'computed', 'image'];

// ─── Single draggable column row ──────────────────────────────────────────────
function ColumnRow({ index, col, onMove, onChange, onRemove }) {
  const ref = useRef(null);

  const [{ isDragging }, drag] = useDrag({
    type: COL_ITEM_TYPE,
    item: { index },
    collect: (m) => ({ isDragging: m.isDragging() }),
  });

  const [, drop] = useDrop({
    accept: COL_ITEM_TYPE,
    hover(item) {
      if (item.index === index) return;
      onMove(item.index, index);
      item.index = index;
    },
  });

  drag(drop(ref));

  const isSystem = ['img', 'created', 'shopify', 'ebay', 'bodyHtml', 'shopifyCategory', 'ebayCategory', 'dimensions'].includes(col.id);

  // Strip the internal prefix so users see "Title" not "consolidatedData.Title"
  const displayFieldName = col.fieldPath?.startsWith('consolidatedData.')
    ? col.fieldPath.slice('consolidatedData.'.length)
    : '';

  const handleFieldNameChange = (e) => {
    const raw = e.target.value;
    onChange(index, { ...col, fieldPath: raw ? `consolidatedData.${raw}` : '' });
  };

  return (
    <Box
      ref={ref}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        py: 0.75,
        px: 1,
        mb: 0.75,
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        opacity: isDragging ? 0.4 : 1,
        bgcolor: col.visible !== false ? 'background.paper' : 'action.disabledBackground',
        cursor: 'grab',
      }}
    >
      <DragIndicatorIcon sx={{ color: 'grey.400', fontSize: 18, flexShrink: 0 }} />

      <TextField
        size="small"
        label="Label"
        value={col.label}
        onChange={(e) => onChange(index, { ...col, label: e.target.value })}
        sx={{ width: 140 }}
      />

      {!isSystem && (
        <TextField
          size="small"
          label="Spec field"
          value={displayFieldName}
          onChange={handleFieldNameChange}
          sx={{ flex: 1, minWidth: 180 }}
          placeholder="e.g. Exterior Material"
        />
      )}

      <FormControl size="small" sx={{ minWidth: 100 }}>
        <InputLabel>Type</InputLabel>
        <Select
          value={col.type || 'text'}
          label="Type"
          onChange={(e) => onChange(index, { ...col, type: e.target.value })}
          disabled={isSystem}
        >
          {COLUMN_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
        </Select>
      </FormControl>

      <Tooltip title={col.visible !== false ? 'Visible' : 'Hidden'}>
        <Switch
          size="small"
          checked={col.visible !== false}
          onChange={(e) => onChange(index, { ...col, visible: e.target.checked })}
        />
      </Tooltip>

      {!isSystem && (
        <Tooltip title="Remove column">
          <IconButton size="small" onClick={() => onRemove(index)} color="error">
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {isSystem && <Box sx={{ width: 30 }} />}
    </Box>
  );
}

// ─── Main panel ───────────────────────────────────────────────────────────────
/**
 * Props:
 *   config   — CategoryConfig
 *   onChange — (updatedConfig) => void
 *   mode     — 'columns' | 'bodyHtml' | undefined (both)
 */
export default function CategoryConfigPanel({ config, onChange, mode }) {
  if (!config) return null;

  const columns = config.columns || [];
  const template = config.bodyHtmlTemplate || {};
  const bulletFields = template.bulletFields || [];

  // ── Column handlers ─────────────────────────────────────────────────────────
  const handleColMove = (from, to) => {
    const next = [...columns];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange({ ...config, columns: next });
  };

  const handleColChange = (index, updated) => {
    const next = [...columns];
    next[index] = updated;
    onChange({ ...config, columns: next });
  };

  const handleColRemove = (index) => {
    onChange({ ...config, columns: columns.filter((_, i) => i !== index) });
  };

  const handleAddColumn = () => {
    const id = `custom_${Date.now()}`;
    onChange({
      ...config,
      columns: [
        ...columns,
        { id, label: 'New Column', fieldPath: '', type: 'text', visible: true, readOnly: false, width: null },
      ],
    });
  };

  // ── Bullet field handlers ───────────────────────────────────────────────────
  const handleBulletsChange = (newFields) => {
    onChange({ ...config, bodyHtmlTemplate: { ...template, bulletFields: newFields } });
  };

  const columnsPanel = (
    <Box sx={{ flex: '1 1 420px', minWidth: 320 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 1 }}>
        {mode === undefined && (
          <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>Table Columns</Typography>
        )}
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Add custom column">
          <IconButton size="small" color="primary" onClick={handleAddColumn}>
            <AddCircleOutlineIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {columns.map((col, idx) => (
        <ColumnRow
          key={col.id}
          index={idx}
          col={col}
          onMove={handleColMove}
          onChange={handleColChange}
          onRemove={handleColRemove}
        />
      ))}

      <Box
        onClick={handleAddColumn}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          py: 0.75,
          px: 1,
          borderRadius: 1,
          border: '1px dashed',
          borderColor: 'primary.main',
          color: 'primary.main',
          cursor: 'pointer',
          mt: 0.5,
          '&:hover': { bgcolor: 'primary.50' },
        }}
      >
        <Typography variant="body2">+ Add Column</Typography>
      </Box>
    </Box>
  );

  const bodyHtmlPanel = (
    <Box sx={{ flex: '1 1 360px', minWidth: 300 }}>
      {mode === undefined && (
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
          Body HTML Fields
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
        Drag to reorder. Special types (dimensions, entropy, etc.) are pre-configured.
      </Typography>
      <BulletFieldEditor fields={bulletFields} onChange={handleBulletsChange} />
    </Box>
  );

  if (mode === 'columns') return columnsPanel;
  if (mode === 'bodyHtml') return bodyHtmlPanel;

  return (
    <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', flexWrap: 'wrap' }}>
      {columnsPanel}
      <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />
      {bodyHtmlPanel}
    </Box>
  );
}
